<template>
  <div class="center" v-if="!firebaseAuth">
    <div class="flex flex-col gap-6 center lg:min-w-[200px]">
      <h2 class="text-white mb-6">Firebase login</h2>
      <div class="flex flex-col gap-1">
        <input type="text" class="bg-transparent text-left border-b border-white text-white focus:outline-none" v-model="email" placeholder="Email">
        <input type="password" class="bg-transparent text-left border-b border-white text-white focus:outline-none" v-model="password" placeholder="Password">
        <button class="w-full button mt-3" @click="login">Login</button>
        <p v-if="loginError" class="mt-3 text-red-600">{{ loginError }}</p>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="center" v-if="!started">
      <button @click="startAlgorithm()" class="start-button mb-4">START 🚀</button>
      <div class="flex flex-col mt-5 gap-3 text-left min-w-[365px]">
        <p class="text-white flex items-center">
          Working with a delay of
          <b><input type="text"
                    v-model.number="options.delay"
                    class="bg-transparent text-center border-b w-[30px] border-white text-white focus:outline-none"
          ></b> seconds
          <span class="relative group ml-3 inline-flex items-center align-middle">
            <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
            <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
              Delay in seconds between the passing and the start of the video. Useful when the screen is placed further from the timing point, so the participant appears at exactly the right moment.
            </span>
          </span>
        </p>

        <p class="text-white flex items-center gap-3">
          <span>Check for watched</span>

          <label class="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              v-model="options.check_for_watched"
              class="sr-only peer"
            >
            <div class="w-11 h-6 bg-gray-400 peer-focus:outline-none rounded-full
                peer peer-checked:bg-green-500
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:rounded-full after:h-5 after:w-5
                after:transition-all
                peer-checked:after:translate-x-5">
            </div>
          </label>
          <span class="relative group ml-2 inline-flex items-center">
            <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
            <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
              When enabled: videos that have already been shown are skipped. When disabled: every video can be played multiple times.
            </span>
          </span>
        </p>

        <button @click="toggleAdvanced" class="text-purple-200 flex items-center gap-2 mt-6">
          Advanced
          <span :class="{'rotate-180': showAdvancedOptions}" class="transition-transform duration-300">
            &#9660;
          </span>
        </button>
        <hr>

        <!-- Geavanceerde opties -->
        <div class="flex flex-col gap-3" v-if="showAdvancedOptions">
          <p class="text-white flex items-center">
            Working with event (event ID)
            <b><input type="text" v-model="event_id"
                      class="bg-transparent text-center border-b w-[125px] border-white text-white focus:outline-none"
            ></b>
            <span class="relative group ml-3 inline-flex items-center align-middle">
              <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
              <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
                The ID of the event to fetch submissions from in Firebase. Must match the event_id in the database (e.g. 1724).
              </span>
            </span>
          </p>
          <p class="text-white flex items-center">
            Working with timeline
            <b><input type="text" v-model="options.timeline"
                      class="bg-transparent text-center border-b w-[125px] border-white text-white focus:outline-none"
            ></b>
            <span class="relative group ml-3 inline-flex items-center align-middle">
              <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
              <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
                The name of the timing point in Tracx to listen to. Only passings with exactly this name will be processed (e.g. CHEER1_UTC).
              </span>
            </span>
          </p>

          <p class="text-white flex items-center">
            Working with method
            <select v-model="options.method" class="bg-transparent text-left border-b font-semibold w-auto border-white text-white focus:outline-none">
              <option value="watchPassings">Watch Passings (Polling)</option>
              <option value="eventSource">EventSource (Live)</option>
            </select>
            <span class="relative group ml-3 inline-flex items-center align-middle">
              <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
              <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
                <b class="block mb-1">Watch Passings:</b> fetches passings from the API every 3 seconds. Stable and reliable.<br><br>
                <b class="block mb-1">EventSource:</b> receives passings in real-time via a live connection. Faster, but more sensitive to connection issues.
              </span>
            </span>
          </p>

          <p class="text-white flex items-center gap-3">
            <span>Mark watched videos</span>

            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="options.mark_watched"
                class="sr-only peer"
              >
              <div class="w-11 h-6 bg-gray-400 peer-focus:outline-none rounded-full
                peer peer-checked:bg-green-500
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:rounded-full after:h-5 after:w-5
                after:transition-all
                peer-checked:after:translate-x-5">
              </div>
            </label>
            <span class="relative group ml-2 inline-flex items-center">
              <span class="cursor-help text-white text-[9px] ring-1 ring-white rounded-full w-3 h-3 inline-flex items-center justify-center leading-none select-none">?</span>
              <span class="pointer-events-none absolute left-5 -top-1 z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 shadow-xl border border-white/10 leading-relaxed">
                Disable this if you want to open this screen as a viewer — so you can watch along without affecting what the main screen shows.
              </span>
            </span>
          </p>
        </div>
      </div>
    </div>

    <div class="relative" v-else>
        <img v-if="!videoIsPlaying" class="default-background-image" src="@/assets/default-background-image.svg">
        <img v-if="videoIsPlaying" class="cheerzone-video-bar" src="@/assets/cheerzone-bar.svg">
        <div v-if="environment === 'dev'" class="absolute z-10 top-6 left-6 bg-black p-2 text-white text-sm flex flex-col gap-1">
          <span>participant_id: {{ activePassing?.participant_id }}</span>
          <span>start_number: {{ activePassing?.start_number }}</span>
          <span>time: {{ activePassing?.time }}</span>
        </div>
        <div class="video-div" id="videoDiv" :style="videoIsPlaying ? 'z-index:2' : 'z-index:0' ">
          <video :src="videoURL" autoplay ref="videoPlayer"/>
          <img class="video-placeholder" v-if="videoURL" src="@/assets/video-placeholder.svg">
        </div>
    </div>
  </div>
</template>

<script>

import {getSubmissionsByStartNumber, updateSubmission, loginWithCredentials, auth} from '@/firebase';
import moment from "moment";
import {urlParameter} from "@/helpers/urlParameter";

export default {
  name: 'videos',
  data() {
    return {
      event_id: urlParameter.get('event') ? urlParameter.get('event') : '1724',
      environment: process.env.NODE_ENV === 'development' ? 'dev' : 'prod',
      showAdvancedOptions: false,
      firebaseAuth: false,
      loginError: null,
      started: false,
      email: "",
      password: "",
      videoPlayer: null,
      videoIsPlaying: false,
      videoURL: null,
      data: [],
      activePassing: null,
      options: {
        check_for_watched: urlParameter.get('check_for_watched') ? urlParameter.get('check_for_watched') : true,
        mark_watched: urlParameter.get('mark_watched') ? urlParameter.get('mark_watched') : true,
        delay: urlParameter.get('delay') ? urlParameter.get('delay') : 0,
        timeline: urlParameter.get('timeline') ? urlParameter.get('timeline') : 'CHEER1_UTC',
        method: urlParameter.get('method') ? urlParameter.get('method') : 'watchPassings',
        passings_cleanup_interval: 10,
      },
      fakeData: [
        {
          "participant_id": 1,
          "start_number": "1",
          "time": "2026-01-28 10:06:00.000000",
          "timeline_name": "CHEER1_UTC"
        },
        {
          "participant_id": 2,
          "start_number": "2",
          "time": "2026-01-28 10:06:03.000000",
          "timeline_name": "CHEER1_UTC"
        },
        {
          "participant_id": 3,
          "start_number": "3",
          "time": "2026-01-28 10:06:13.000000",
          "timeline_name": "CHEER1_UTC"
        },
      ]
    }
  },
  mounted() {
    this.syncAllURLParameters();

    if (process.env.NODE_ENV === 'development') {
      this.firebaseAuth = true;
    } else {
      auth.onAuthStateChanged(user => {
        if (user && !user.isAnonymous) {
          this.firebaseAuth = true;
        }
      });
    }
  },
  watch: {
    'options.delay'() {
      this.syncAllURLParameters();
    },
    'options.check_for_watched'() {
      this.syncAllURLParameters();
    },
    'options.mark_watched'() {
      this.syncAllURLParameters();
    },
    'options.timeline'() {
      this.syncAllURLParameters();
    },
    'options.method'() {
      this.syncAllURLParameters();
    },
    'event_id'() {
      this.syncAllURLParameters();
    }
  },
  methods: {
    syncAllURLParameters() {
      const url = new URL(window.location);
      url.searchParams.set('delay', this.options.delay);
      url.searchParams.set('check_for_watched', this.options.check_for_watched);
      url.searchParams.set('mark_watched', this.options.mark_watched);
      url.searchParams.set('timeline', this.options.timeline);
      url.searchParams.set('method', this.options.method);
      url.searchParams.set('event', this.event_id);

      window.history.replaceState({}, '', url);
    },
    async login() {
      try {
        this.loginError = null;
        this.firebaseAuth = await loginWithCredentials(this.email, this.password);
        if (!this.firebaseAuth) {
          this.loginError = "Login failed. Please try again.";
        }
      } catch (error) {
        console.error("Login error:", error);
        this.loginError = error.message || "An unexpected error occurred.";
      }
    },
    toggleAdvanced() {
      this.showAdvancedOptions = !this.showAdvancedOptions;
    },
    startAlgorithm() {
      this.started = true;
      console.info("Working in " + process.env.NODE_ENV + " mode");

      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          this.videoPlayer = this.$refs.videoPlayer;
          this.videoPlayer.addEventListener('ended', this.videoEnded);
        } else {
          console.error('videoPlayer ref is niet gevonden');
        }
      });

      if (this.options.method === 'eventSource') {
        this.listenToPassings();
      } else if (this.options.method === 'watchPassings') {
        // Start watching passings (passings_interval)
        setInterval(() => {
          if (process.env.NODE_ENV === 'development') {
            this.reformPassings(this.fakeData);
          } else {
            this.getPassings().then(passings => {
              console.info('Reveived passings: ', passings);
              if (passings.length === 0) {
                return;
              }
              this.reformPassings(passings);
            });
          }
        }, 3000);
      }

      this.processPassings();
      this.removeOldPassing();
    },
    listenToPassings() {
      const eventSource = new EventSource(this.$api.app.defaults.baseURL + 'v1/web/webhook/passings/sse');
      eventSource.onmessage = (event) => {
        console.log('Message from server:', event);
        if(event.data) {
          try {
            const jsonData = JSON.parse(event.data);
            this.reformPassings(jsonData);
          } catch (error) {
            console.error("Data: ", event.data, "is invalid JSON: ", error);
          }
          this.reformPassings(event.data);
        }
      };
      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
      };
    },
    removeOldPassing() {
      const now = moment.utc();

      this.data = this.data.filter(passing => {
        const dif = now.diff(passing.videoStart);
        if (dif > 60000) {
          console.info('Removed this passing - ', passing, ' - Older then 1 minute');
        }
        return dif <= 60000;
      });

      setTimeout(() => this.removeOldPassing(), this.options.passings_cleanup_interval * 1000);
    },
    async getPassings() {
      try {
        const response = await this.$api.app.get('v1/web/webhook/passings');
        return response.data.data;
      } catch (error) {
        console.error('Error fetching passings:', error);
      }
    },
    reformPassings(data) {
      data.forEach((passing) => {
        const videoStart = moment.utc(passing.time).add((this.options.delay * 1000), 'milliseconds');
        passing.videoStart = videoStart;
        passing.videoStartFormat = videoStart.format;

        // We need a timeline name
        if (this.options.timeline) {
          if (passing.timeline_name && (passing.timeline_name.toString() === this.options.timeline)) {
            this.data.push(passing);
          }
        }
      });
      console.info('Reformed passings to: ', this.data);
    },
    async processPassings() {
      const now = moment.utc();
      let activeIndex
      const activePassing = this.data.find((item, index) => {
        if (!item) {
          return;
        }
        let videoStart = item.videoStart;
        activeIndex = index;

        return now.clone().subtract(1, 'seconds').isSameOrBefore(videoStart) && now.clone().add(1, 'seconds').isSameOrAfter(videoStart);
      });

      if (null === activePassing || undefined === activePassing) {
        this.activePassing = null;
        setTimeout(() => this.processPassings(), 1000);
      }

      if (activePassing) {
        let submissions = await getSubmissionsByStartNumber(activePassing.start_number.toString(), this.event_id);

        if (submissions && submissions.length) {
          // Search for unwatched submission
          let submission = submissions.find(sub => sub.watched === false);

          // If there isn't any, take the first one.
          if (!submission) {
            submission = submissions[0];
          }

          if (submission && (!this.options.check_for_watched || !submission.watched)) {
            this.activePassing = activePassing;
            this.videoURL = submission.video;
            this.playVideo();
            this.markPassingAsShown(submission);
          } else {
            delete this.data[activeIndex];
            this.processPassings();
          }
        } else {
          delete this.data[activeIndex];
          this.processPassings();
        }
      }
    },
    markPassingAsShown(submission) {
      if(this.options.mark_watched) {
        updateSubmission(submission.id, {watched: true})
      }
    },
    playVideo() {
      if (!this.videoPlayer) return;

      this.videoIsPlaying = true;
      this.videoPlayer.load();
    },
    videoEnded() {
      this.videoIsPlaying = false;
      this.videoURL = null;

      setTimeout(this.processPassings, 200);
    }
  }
}
</script>
